"use client";

import { Popover as PopoverPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Domyslny kontener portalu dla popoverow wewnatrz modalnej warstwy. Ustawia go
 * `BottomSheet` (filtry na telefonie), wiec wybieraki z polem szukania dzialaja
 * w arkuszu bez przekazywania `portalContainer` przez kazda strone.
 */
export const PopoverPortalContext = React.createContext<HTMLElement | null>(null);

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
	return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
	return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

/**
 * PopoverContent.
 *
 * @param portalContainer - Opcjonalny container dla Portal (domyslnie document.body).
 * Kluczowe gdy Popover jest wewnatrz Radix Dialog — bez `container` wskazujacego
 * na DialogContent, Dialog's FocusScope traktuje Popover (renderowany w
 * document.body via Portal) jako "outside" i przejmuje focus z jego inputow.
 * Ref: https://github.com/radix-ui/primitives/issues/922
 */
function PopoverContent({
	className,
	align = "center",
	sideOffset = 4,
	portalContainer,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
	portalContainer?: HTMLElement | null;
}) {
	const inheritedContainer = React.useContext(PopoverPortalContext);
	return (
		<PopoverPrimitive.Portal container={portalContainer ?? inheritedContainer ?? undefined}>
			<PopoverPrimitive.Content
				data-slot="popover-content"
				align={align}
				sideOffset={sideOffset}
				className={cn(
					"z-50 w-72 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-md outline-hidden",
					"data-[state=open]:animate-in data-[state=closed]:animate-out",
					"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
					"data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
					"data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
					"data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					className,
				)}
				{...props}
			/>
		</PopoverPrimitive.Portal>
	);
}

/** Kotwica bez przełączania (np. pole z podpowiedziami, które samo steruje `open`). */
function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
	return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
